/**
 * A second producer of `SaveBarState` — this one backed by a proposal rather
 * than by inline form edits.
 *
 * The record surface already renders a floating `SaveBar` fed by
 * `SaveBarState`, and that bar self-hides when `changeCount` is 0. Its only
 * producer until now was `OverviewEditSection`, whose Save writes CRM events.
 * A draft needs the same footer with a different commit: approving the pending
 * proposal, which is what actually creates or updates the record.
 *
 * Producing the same shape means no second footer component, no second set of
 * layout rules, and no divergence in how a pending change is presented.
 */

import { useCallback, useMemo, useState } from 'react';

import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import {
  useApproveProposal,
  useRejectProposal,
} from '@/lib/proposals/query/mutations';

import type { SaveBarState } from '../types';

export interface UseDraftSaveBarStateArgs {
  orgId: number | undefined;
  /** The pending proposal to commit. Undefined when nothing is proposed. */
  proposalId: number | undefined;
  /**
   * How many properties the proposal sets. Drives the bar's label and, because
   * `SaveBar` hides itself at 0, whether it appears at all.
   */
  changeCount: number;
  /** Called after a successful approve — e.g. to refresh the host's view. */
  onApproved?: () => void;
  /**
   * Called after a successful reject (Cancel). The host needs the same
   * refresh as after an approve: a chat-built proposal gets no websocket
   * update, so without a re-read the conversation's own copy of the
   * proposal keeps reading "pending" after it has been discarded.
   */
  onRejected?: () => void;
  /**
   * Raw property type names of the changed fields (e.g. `['ahoy_name', 'ahoy_email']`).
   * When combined with `entityTypeName`, the hook resolves human-readable labels
   * for the SaveBar tooltip.
   */
  proposedPropertyNames?: string[];
  /** Entity type name used to fetch the property schema for label resolution. */
  entityTypeName?: string;
}

export function useDraftSaveBarState({
  orgId,
  proposalId,
  changeCount,
  onApproved,
  onRejected,
  proposedPropertyNames,
  entityTypeName,
}: UseDraftSaveBarStateArgs): SaveBarState | null {
  const { mutateAsync: approveAsync } = useApproveProposal(orgId ?? 0);
  const { mutateAsync: rejectAsync } = useRejectProposal(orgId ?? 0);
  // With nothing changed there is nothing to label, so no schema fetch is
  // needed — passing '' here trips `usePropertySchema`'s own
  // `enabled: !!orgId && !!entityTypeName` gate, suppressing the request
  // rather than just discarding a result nobody asked for.
  const hasProposedProperties =
    proposedPropertyNames !== undefined && proposedPropertyNames.length > 0;
  const { schema } = usePropertySchema(
    orgId ?? 0,
    hasProposedProperties ? (entityTypeName ?? '') : ''
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = useCallback(async () => {
    if (proposalId === undefined) {
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await approveAsync({ proposalId });
      onApproved?.();
    } catch (caught) {
      // Surfaced through the bar's own error slot rather than a toast, so the
      // failure stays attached to the action that caused it.
      setError(
        caught instanceof Error ? caught.message : 'Could not save changes'
      );
    } finally {
      setIsSaving(false);
    }
  }, [approveAsync, proposalId, onApproved]);

  const handleCancel = useCallback(async () => {
    if (proposalId === undefined) {
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await rejectAsync({ proposalId });
      onRejected?.();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Could not discard changes'
      );
    } finally {
      setIsSaving(false);
    }
  }, [rejectAsync, proposalId, onRejected]);

  const changedFieldLabels = useMemo(() => {
    if (!proposedPropertyNames || proposedPropertyNames.length === 0) {
      return undefined;
    }
    return proposedPropertyNames.map(name => schema[name]?.label ?? name);
  }, [proposedPropertyNames, schema]);

  return useMemo(() => {
    if (proposalId === undefined || changeCount === 0) {
      // Nothing pending — no footer. Returning null rather than a zero-count
      // state keeps the host's `saveBarState &&` guard meaningful.
      return null;
    }
    return {
      changeCount,
      onSave: handleSave,
      onCancel: () => {
        void handleCancel();
      },
      isSaving,
      error,
      changedFieldLabels,
    };
  }, [
    proposalId,
    changeCount,
    handleSave,
    handleCancel,
    isSaving,
    error,
    changedFieldLabels,
  ]);
}
